/**
 * DashboardPage — Tagesueberblick fuer Simon und Admins (Phase 4 Plan 03).
 *
 * Laedt GET /dashboard/metrics beim Mount und refresht alle 60 Sekunden.
 * Layout: responsive Grid mit 5 KPI-Sections.
 *
 * DASH-01: openPerColumn — 7-Spalten-Counter als Vertikal-Liste
 * DASH-02: conversionRate — KpiCard mit monthlyPercent + 3-Monats-Trend
 * DASH-03: avgDaysPerColumn — AvgDaysBarChart
 * DASH-04: leadTimeline — LeadTimelineChart (30 Tage)
 * DASH-05: hotleadRate — KpiCard mit % + count/total Subtitle
 *
 * CI: #E9FF57 Akzent, #1A1A1A Text, #DAD9D9 Grid/Border. NIE weiss auf Gelb.
 */

import { useEffect, useState } from 'react';
import { leadsApi } from '../services/leadsApi';
import type { DashboardMetrics } from '../types';
import KpiCard from '../components/dashboard/KpiCard';
import LeadTimelineChart from '../components/dashboard/LeadTimelineChart';
import AvgDaysBarChart from '../components/dashboard/AvgDaysBarChart';

const STATUS_LABELS: Record<string, string> = {
  lead_form: 'Lead-Formular',
  followup_1: 'Follow-up 1',
  followup_2: 'Follow-up 2',
  in_kontakt: 'In Kontakt',
  positiv: 'Positiv',
  negativ: 'Negativ',
  negativ_hotlead: 'Neg. Hotlead',
};

const STATUS_ORDER = [
  'lead_form',
  'followup_1',
  'followup_2',
  'in_kontakt',
  'positiv',
  'negativ',
  'negativ_hotlead',
];

export default function DashboardPage() {
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      setError(null);
      const data = await leadsApi.dashboard.getMetrics();
      setMetrics(data);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unbekannter Fehler';
      setError(`KPIs konnten nicht geladen werden: ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const interval = setInterval(load, 60 * 1000); // Auto-Refresh alle 60s
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-[#DAD9D9] border-t-[#1A1A1A]" />
        <span className="ml-3 text-[#1A1A1A]">Lade KPIs...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded text-sm text-red-700">
        {error}
      </div>
    );
  }

  if (!metrics) return null;

  const totalLeads = metrics.hotleadRate.totalLeads;
  const isEmpty = totalLeads === 0;

  if (isEmpty) {
    return (
      <div className="p-8 text-center text-gray-500 border border-[#DAD9D9] rounded-lg bg-white">
        Noch keine Leads — Dashboard fuellt sich nach den ersten Eintraegen
      </div>
    );
  }

  const conversionTrend =
    metrics.conversionRate.trailing3MonthsPercent - metrics.conversionRate.monthlyPercent;

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold text-[#1A1A1A]">Dashboard</h2>

      {/* Row 1: 3 KpiCards — Eingang + Positiv + Conversion */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <KpiCard
          label="Eingang 30 Tage"
          value={metrics.conversionRate.totalIncoming30d}
          subtitle="Neue Leads (letzten 30 Tage)"
        />
        <KpiCard
          label="Positiv 30 Tage"
          value={metrics.conversionRate.totalPositive30d}
          subtitle="Positiv abgeschlossen (letzten 30 Tage)"
        />
        <KpiCard
          label="Conversion %"
          value={`${metrics.conversionRate.monthlyPercent.toFixed(1)} %`}
          subtitle={`3-Monats-Trend: ${metrics.conversionRate.trailing3MonthsPercent.toFixed(1)} %`}
          trend={conversionTrend}
        />
      </div>

      {/* Row 2: Timeline-Chart + Hotlead-Quote */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-lg border border-[#DAD9D9] bg-white p-4">
          <h3 className="text-sm font-semibold text-[#1A1A1A] mb-3">
            Lead-Zugang — letzte 30 Tage (DASH-04)
          </h3>
          <LeadTimelineChart data={metrics.leadTimeline} />
        </div>
        <KpiCard
          label="Hotlead-Quote"
          value={`${metrics.hotleadRate.percent.toFixed(1)} %`}
          subtitle={`${metrics.hotleadRate.hotleadCount} von ${metrics.hotleadRate.totalLeads} Leads`}
        />
      </div>

      {/* Row 3: Verweildauer-BarChart + Spalten-Counter */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-lg border border-[#DAD9D9] bg-white p-4">
          <h3 className="text-sm font-semibold text-[#1A1A1A] mb-3">
            Ø Verweildauer pro Spalte in Tagen (DASH-03)
          </h3>
          <AvgDaysBarChart data={metrics.avgDaysPerColumn} />
        </div>

        {/* 7-Spalten-Counter (DASH-01) */}
        <div className="rounded-lg border border-[#DAD9D9] bg-white p-4">
          <h3 className="text-sm font-semibold text-[#1A1A1A] mb-3">
            Offene Leads pro Spalte (DASH-01)
          </h3>
          <ul className="space-y-2">
            {STATUS_ORDER.map((status) => {
              const count = metrics.openPerColumn[status] ?? 0;
              return (
                <li key={status} className="flex items-center justify-between text-sm">
                  <span className="text-[#1A1A1A]">{STATUS_LABELS[status] ?? status}</span>
                  <span
                    className={`font-bold text-base ${count > 0 ? 'text-[#1A1A1A]' : 'text-gray-400'}`}
                  >
                    {count}
                  </span>
                </li>
              );
            })}
          </ul>
          <div className="mt-3 pt-2 border-t border-[#DAD9D9] flex justify-between text-xs font-semibold text-[#1A1A1A]">
            <span>Gesamt</span>
            <span>{Object.values(metrics.openPerColumn).reduce((a, b) => a + b, 0)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
